import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/app/common/components/Card'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import Textarea from '@/app/common/components/Textarea'
import { ProviderPreviewSettings } from '@/schemas/provider.schema'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import { useUpdatePreviewSettings } from '../hooks/useUpdatePreviewSettings'
import AdminAuthGuard from '../../../admin-auth-guard'

const companyInfoSchema = z.object({
  name: z.string().nullable().optional(),
  legal_name: z.string().optional(),
  organizationDescription: z
    .string()
    .max(200, 'Description must be less than 200 characters')
    .optional(),
})

export type CompanyInfoFormData = z.infer<typeof companyInfoSchema>

export interface CompanyInfoCardProps {
  provider: ProviderPreviewSettings
  providerId: string
}

const CompanyInfoCard = ({ provider, providerId }: CompanyInfoCardProps) => {
  const programId = useSelector(selectSelectedProgram).id

  const storeMutation = useUpdatePreviewSettings({
    providerId,
  })
  const form = useForm<CompanyInfoFormData>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      name: provider.name ?? '',
      legal_name: provider.name ?? '',
      organizationDescription: provider.organization_description ?? '',
    },
  })

  const onSubmit = (data: CompanyInfoFormData) => {
    storeMutation.mutate({
      program_id: programId,
      name: data.name,
      legal_name: data.legal_name,
      organization_description: data.organizationDescription,
    })
  }

  return (
    <Card id="company-info">
      <CardHeader>
        <h3 className="text-lg font-semibold text-gray-900">
          Company Information
        </h3>
      </CardHeader>
      <CardContent>
        <form id="company-info-form" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">
                Organization Name (To display in Marketplace):
              </Label>
              <Input className="w-full" id="name" {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="company_name_legal">
                Organization Name (Legal):
              </Label>
              <Input id="company_name_legal" {...form.register('legal_name')} />
              {form.formState.errors.legal_name && (
                <p className="mt-1 text-sm text-red-600">
                  {form.formState.errors.legal_name.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="organizationDescription">
                Organization Description (Optional)
              </Label>
              <Textarea
                {...form.register('organizationDescription')}
                name="organizationDescription"
                rows={3}
                placeholder="Enter organization description"
              />
              {form.formState.errors.organizationDescription && (
                <p className="mt-1 text-sm text-red-600">
                  {form.formState.errors.organizationDescription.message}
                </p>
              )}
            </div>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex items-center justify-end">
        <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
          <SubmitButton
            className="w-auto"
            buttonTxt="Save changes"
            form="company-info-form"
            status={storeMutation.status}
          />
        </AdminAuthGuard>
      </CardFooter>
    </Card>
  )
}

export default CompanyInfoCard
